import comm from "csi/comm";
import JsonResponse from "csi/comm/JsonResponse";
import Request from "csi/comm/Request";
import reflection from "csi/reflection";
import { resolveNode } from "csi/service/nodeService";
import sessionConstants from "csi/sessionConstants";
import { rootCache } from "csi/state/Cache";
import StoreCache from "csi/store/Cache";
import StoreNamesConstructor, { StoreNames } from "csi/store/Names";
import { C8Node, DUMMY, cnl } from "csi/typings";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { action, makeObservable, observable } from "mobx";
import { RootStore } from "./rootStore";

export default class SessionStore {
	@observable private _adminOnly = false;

	@observable private _hasNewConversationNotifications: boolean = false;
	@observable private _hasNewMessageNotifications: boolean = false;
	@observable private _hasNewTaskNotifications: boolean = false;

	@observable.ref private _lastVisited: cnl[] = [];

	@observable private _locale: string = "";

	/** max number of last visited items to track */
	private maxLastVisited: number = 10;

	@observable private _mergedLocale = false;

	@observable private _notificationCount: number = 0;
	@observable private _notificationDropdownIsOpen: boolean = false;

	/** store containing playable roles */
	@observable.ref playableRolesStore: StoreNames;

	private readonly rootStore: RootStore;

	@observable private _skinTheme: string = "SkinTheme:CentricBlue";

	/** indicates if user is in administrator group */
	get admin(): boolean {
		return sessionConstants.admin;
	}

	/** indicates if only administrator can use system */
	get adminOnly(): boolean {
		return this._adminOnly;
	}

	/** allow actions to create nodes per group when BOM is grouped by type */
	allowBomGroupByTypeNewActions = false;

	/** number of Camunda tasks assigned to the current user */
	@observable camundaAssignedTaskCount = 0;

	get currentUser(): C8Node {
		return rootCache.get(sessionConstants.userUrl);
	}

	/** indicates if debug mode is enabled */
	@observable debugMode = false;

	/** level of top most dialog; zero if no dialogs opened */
	dialogLevel = 0;

	/** compare independently, not solely relative to the origin */
	disableCompareRelativeToOrigin = false;

	/** indicates if drag and drop is in progress or not */
	@observable dndInProgress = false;

	/** indicates if context-sensitive search is allowed from the breadcrumb */
	@observable enableBreadcrumbSearch = true;

	/** indicates if rendering to excel */
	excel = false;

	/** indicates if view must not use resizeTimeout but resize (call _onResize) instantly */
	forceResize = false;

	/** indicate if new conversation notifications are coming through */
	get hasNewConversationNotifications(): boolean {
		return this._hasNewConversationNotifications;
	}

	/** indicate if new conversation notifications are coming through */
	get hasNewMessageNotifications(): boolean {
		return this._hasNewMessageNotifications;
	}

	/** indicate if new task notifications are coming through */
	get hasNewTaskNotifications(): boolean {
		return this._hasNewTaskNotifications;
	}

	/** help document for site */
	helpFile = "";

	/** landing page url for user when logs in */
	get homeUrl(): string {
		return sessionConstants.userCompanyUrl || reflection.URL_HOME;
	}

	/** checks if the current user is an agent user */
	get isAgentUser(): boolean {
		return !!this.userCompany?.IsAgent;
	}

	/** last element on which action was invoked */
	lastActionElement: Element | null = null;

	/** last time action ended (in milliseconds) - used to detect if repeating the same action */
	lastActionEndTime: number | null = null;

	/** identifier of last action invoked */
	lastActionId = "";

	/** last view on which action was invoked */
	lastActionView = null;

	/** user's selected locale */
	get locale(): string {
		return this._locale;
	}

	/** List of last visited CNLs */
	get lastVisited(): cnl[] {
		return this._lastVisited;
	}

	/** indicates if merged configuration was loaded */
	mergedConfig = false;

	/** indicates if merged locale was loaded */
	get mergedLocale(): boolean {
		return this._mergedLocale;
	}

	/** minimum font scaling allowed when printing */
	minimumFontScaling = 0.6;

	/** indicates view which is currently being edited that should disabling all changes elsewhere */
	@observable modalView = null;

	/** indicates if there is an active action that is modeless (can be interrupted by other actions) */
	@observable modelessAction = false;

	/** number of new notifications */
	get notificationCount(): number {
		return this._notificationCount;
	}

	/** indicate if the dropdown of notification is open */
	get notificationDropdownIsOpen(): boolean {
		return this._notificationDropdownIsOpen;
	}

	/** non-observable global cache (used by csi/resolver) */
	readonly nonObservableCache = new StoreCache();

	/** number of Proofing tasks assigned to the current user */
	@observable proofingAssignedTaskCount = 0;

	/** indicates if printing (to pdf or browser) */
	@observable print = false;

	/** indicates if an ad-hoc print (to pdf or browser) rather than being server-initiated */
	@observable printAdHoc = false;

	/** indicates if auto-scaling is enabled for current print view */
	printAutoScale = false;

	/** indicates if print of a view is complete which can be used to disable certain functionality */
	@observable printDone = false;

	/** available paper height in pixels when printing (to pdf or browser) */
	printHeight = 0;

	/** page scale percentage (0-1) when printing */
	printScale = 0;

	/** print large images with viewable to get best image quality */
	printViewableImage = false;

	/** available paper width in pixels when printing (to pdf or browser) */
	printWidth = 0;

	/** role being played */
	get role(): C8Node | undefined {
		return rootCache.get(sessionConstants.roleUrl);
	}

	/** preferences for role being played */
	getRolePreferences = (url?: cnl): C8Node | undefined => {
		const role = rootCache.get(url || sessionConstants.roleUrl);
		return rootCache.get(role?.Preferences);
	};

	/** indicates if help pane is currently displayed */
	@observable showHelp = false;

	/** CompanyInfo node for site */
	get siteCompany(): C8Node {
		return rootCache.get(reflection.URL_COMPANY);
	}

	/** default locale for site */
	get siteLocale(): string {
		return sessionConstants.siteLocale;
	}

	/** site preferences node */
	get sitePreferences(): C8Node {
		return rootCache.get(reflection.URL_SITE_PREFERENCES);
	}

	/** setup url for current user */
	get siteUrl(): string {
		return sessionConstants.userCompanyInfoUrl || reflection.URL_SITE;
	}

	/** the latest skin theme */
	get skinTheme(): string {
		return this._skinTheme;
	}

	/** the Slack Bot User Id if the user is authenticated against Slack */
	slackBotUserId: string = null;

	/** the Slack User Id if the user is authenticated against Slack */
	slackUserId: string = null;

	/** logged in user */
	get user(): C8Node {
		return rootCache.get(this.userUrl);
	}

	/** company to which user belongs, if any */
	get userCompany(): C8Node {
		return rootCache.get(sessionConstants.userCompanyUrl);
	}

	/** Context for current user, including chosen locale (mainly used for determining query specs for attributes when no other context is provided) */
	userContext = null;

	/** logged in user */
	get userDefaults(): C8Node {
		const { userDefaultsUrl } = this;
		return rootCache.get(userDefaultsUrl);
	}

	/** URL of current user defaults */
	get userDefaultsUrl(): cnl {
		// NOTE: user could be undefined before session is initialized, and this is used as part of a reaction so it gets called early
		return this.user?.Defaults || DUMMY;
	}

	/** current user defaults node */
	get userDetails(): C8Node {
		const { userDetailsUrl } = this;
		return rootCache.get(userDetailsUrl);
	}

	/** URL of current user details */
	get userDetailsUrl(): cnl {
		return this.user.Details;
	}

	/** name of current user */
	get userName(): string {
		return this.user.$Name;
	}

	/** URL of current user preferences */
	get userPreferencesUrl(): cnl {
		return this.user.Preferences;
	}

	/** current user preferences node */
	get userPreferences(): C8Node {
		const { userPreferencesUrl } = this;
		return rootCache.get(userPreferencesUrl);
	}

	/** type of node owning current user */
	get userType(): string {
		return sessionConstants.userType;
	}

	/** URL of current user */
	get userUrl(): cnl {
		return sessionConstants.userUrl;
	}

	/** indicate if WebSocket channel is initialized without errors */
	websocketsEnabled: boolean = false;

	/** Grabs the userDetails node from the database and loads it into cache */
	async refreshUserDetails(): Promise<C8Node> {
		const node = await resolveNode(this.userDetailsUrl);
		rootCache.add(node);
		return node;
	}

	/** return node with given url from cache */
	getNode = (url: cnl): C8Node => {
		return rootCache.get(url);
	};

	resetPreferences = (): Promise<JsonResponse> => {
		const { playableRolesStore } = this;
		const urls = [reflection.URL_SITE_PREFERENCES];

		playableRolesStore.internalQuery()
			.forEach((item) => {
				const role = rootCache.get(playableRolesStore.getIdentity(item) as string);
				if (role) {
					urls.push(role.Preferences);
				}
			});

		const request = comm.getNodeRequest(urls);
		return this.submitRequest(request);
	};

	/** submit request to server and update cache with response */
	submitRequest = async (request: Request, ioArgs?: object): Promise<JsonResponse> => {
		const response = await request.go(ioArgs);

		rootCache.cacheResponse(response);

		return response;
	};

	/** submit operations to server */
	submitWriter = async (writer: Writer): Promise<JsonResponse> => {
		return this.submitRequest(writer.finalizeRequest());
	};

	/** update last visited list with new url ensuring no duplicates and does not exceed 10 nodes */
	@action addLastVisited = (url: cnl): void => {
		let a = this._lastVisited;
		if (a[0] != url) {
			a = a.filter((e) => e != url);
			a.unshift(url);
			while (a.length > this.maxLastVisited) {
				a.pop();
			}
			this._lastVisited = a;
		}

		const writer = new Writer();
		writer.addParameter("AsyncRequest", "True");
		writer.callMethod("SiteAdmin", "AddLastVisited")
			.addParameter("URL", xml.ATTR_REF, url);

		try {
			this.submitWriter(writer);
		} catch (err) {
			// ignore
		}
	};

	@action setAdminOnly(adminOnly: boolean): void {
		this._adminOnly = adminOnly;
	}

	@action setHasNewConversationNotifications = (hasNew: boolean): void => {
		this._hasNewConversationNotifications = hasNew;
	};

	@action setHasNewMessageNotifications = (hasNew: boolean): void => {
		this._hasNewMessageNotifications = hasNew;
	};

	@action setHasNewTaskNotifications = (hasNew: boolean): void => {
		this._hasNewTaskNotifications = hasNew;
	};

	@action setLastVisited = (lastVisited: cnl[]): void => {
		this._lastVisited = lastVisited ?? [];
	};

	@action setLocale = (locale: string): void => {
		this._locale = locale;
	};

	@action setMergedLocale = (mergedLocale: boolean): void => {
		this._mergedLocale = mergedLocale;
	};

	@action setNotificationCount = (count: number): void => {
		this._notificationCount = count;
	};

	@action setNotificationDropdownIsOpen = (open: boolean): void => {
		this._notificationDropdownIsOpen = open;
	};

	@action setPlayableRoles = (roles: C8Node[]): void => {
		this.playableRolesStore = StoreNamesConstructor.createFromNodes(roles);
	};

	@action setSkinTheme = (skinTheme: string): void => {
		this._skinTheme = skinTheme;
	};

	constructor(rootStore: RootStore) {
		makeObservable(this);
		this.rootStore = rootStore;
	}
}
